<template>
  <main class="main main--has-floating-buttons">
    <portal to="headerTitle">
      {{ $t('admin.pageInHouseService.title') }}
    </portal>
    <template v-if="inHouseServices !== null">
      <template v-for="ihsType in enums['InHouseServiceType']">
        <card :title="$t(`InHouseServiceType.${ihsType}`)"
              :key="ihsType"
              :collapsed="ihsType !== 'Others'"
              cols="12"
              card-type="collapsible">
          <hm-card-inner :full-size="true">
            <hm-col cols="12">
              <template v-for="inHouseService in inHouseServices" v-if="inHouseService.type === ihsType && !inHouseService.deleted">
                <hm-card-inner-line :title="getName(inHouseService)" :clickable="true" :key="inHouseService.id" @click="routeToInHouseService(inHouseService)" />
              </template>
            </hm-col>
          </hm-card-inner>
        </card>
      </template>

      <card v-if="!isEmpty(deletedInHouseServices)" :title="$t('admin.pageInHouseService.card.deletedInHouseServices')" cols="12" card-type="collapsible">
        <hm-card-inner :full-size="true">
          <hm-card-inner-line v-for="inHouseService in deletedInHouseServices"
                              :title="getName(inHouseService)"
                              :clickable="true"
                              :key="inHouseService.id"
                              @click="routeToInHouseService(inHouseService)" />
        </hm-card-inner>
      </card>
    </template>

    <hm-floating-buttons id="actions" ref="floatingButtons">
      <hm-floating-button :label="$t('admin.pageInHouseServices.actions.more')" icon="plus" color="blue">
        <hm-floating-child-button :label="$t('admin.pageInHouseServices.actions.addInHouseService')" icon="plus" color="blue" @click="addInHouseService('Others')" />
      </hm-floating-button>
    </hm-floating-buttons>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import vue2Dropzone from 'vue2-dropzone';
import draggable from 'vuedraggable';
import { cloneDeep, flattenDeep, groupBy, isEmpty, isNil, keyBy, sortBy } from 'lodash';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';

export default {
  name: 'PageInHouseServices',

  components: {
    draggable,
  },

  autosave: [
    {
      name: 'inHouseServices',
      variable: 'sortedInHouseServiceIds',
      event: 'hotelSettings.update',
      fn: function (cbSaveComplete) {
        const update = {
          inHouseServiceIds: this.sortedInHouseServiceIds,
        };

        this.axios.put(this.serviceUrls.inHouseService.sort(this, this.$route.params.inHouseServiceId), update)
          .then((response) => {
            this.showMessages(response);
            cbSaveComplete();
            this.$serviceBus.$emit('inHouseService.list', this.inHouseServices);
          })
          .catch((error) => {
            this.showMessages(error.response);
            cbSaveComplete();
          });
      },
    },
  ],

  mixins: [HotelmeisterMixin, SaveWatchMixin],

  data() {
    return {
      inHouseServices: null,
    };
  },

  computed: {
    deletedInHouseServices: function () {
      return isNil(this.inHouseServices) ? null : this.inHouseServices.filter(it => it.deleted === true);
    },
    enums: function () {
      return this.$store.getters['enums/enums'];
    },
  },

  watch: {
  },

  created: function () {
    this.loadData();
  },

  updated() {
  },

  beforeDestroy() {
  },

  methods: {
    loadData() {
      this.axios.get(this.serviceUrls.inHouseService.list(this))
        .then((response) => {
          this.inHouseServices = sortBy(response.data.data, obj => obj.name[this.hotelSettings.details.primaryLanguage]);

          this.$serviceBus.$emit('inHouseService.list', this.inHouseServices);
        });
    },
    addInHouseService(type = 'Others') {
      this.axios.post(this.serviceUrls.inHouseService.list(this), {
        name: {
          [this.hotelSettings.details.primaryLanguage]: this.$t('admin.pageInHouseServices.newInHouseServiceName'),
        },
        description: {
          [this.hotelSettings.details.primaryLanguage]: '',
        },
        type: isEmpty(type) ? 'Others' : type,
        inHouseServiceMode: 'Regular',
        applicableTaxes: [],
        ageCategories: [],
        level: 'RatePlan',
        rateMode: 'PerRoom',
        rateFrequency: 'PerStay',
        deleted: false,
      })
        .then((response) => {
          this.loadData();
          this.$serviceBus.$emit('hotelSettings.update');
        })
        .catch((error) => {
          if (isNil(error.response)) this.showErrorMessage();
          this.showMessages(error.response);
        });
    },
    getName(inHouseService) {
      const name = isNil(inHouseService) ? null : inHouseService.name;
      if (isNil(name)) return '-';
      let ret = null;

      ret = name[this.$shortLocale] || name[this.hotelSettings.details.primaryLanguage];

      let details = '';

      if (inHouseService.deleted === false) {
        if (!isNil(inHouseService.amount) && inHouseService.amount !== 0) {
          details += `, ${inHouseService.amount.toLocaleString(this.$i18n.locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} ${this.hotelSettings.details.currency}`;
        }

        if (inHouseService.rateMode === 'PerPerson') {
          details += ` ${this.$t('RateMode.PerPerson')}`;
        }

        if (inHouseService.rateFrequency === 'PerNight') {
          details += ` ${this.$t('RateFrequency.PerNight')}`;
        }

        if (!isEmpty(inHouseService.applicableTaxes)) {
          inHouseService.applicableTaxes.forEach((taxId) => {
            details += `, ${this.findHotelSettingById('taxes', taxId).name[this.hotelSettings.details.primaryLanguage]}`;
          });
        }
      }

      if (!isNil(ret)) {
        ret = ret.trim();
      }

      if (isEmpty(ret)) {
        ret = '-';
      }

      ret = `${ret}<span class="color--gray">${details}</span>`;

      if (!isEmpty(ret)) return ret;

      return name[this.hotelSettings.details.primaryLanguage];
    },
    routeToInHouseService(inHouseService) {
      this.$router.push({
        name: 'inHouseService',
        params: {
          inHouseServiceId: inHouseService.id,
        },
      });
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
</style>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/pages/admin/PageInHouseServices.vue